import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RequestParams, ApiResponse } from '@/shared/api';
import type {
  BrandBase,
  BrandById,
  BrandCreatePayload,
  BrandUpdatePayload,
  BrandItem,
  BrandItemListParams,
} from './types';
import { BRAND_KEYS } from '../lib';
import type { ApiResponseList } from '@/shared/api/types';
import { BrandApi } from '../api';

export const useBrandList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<BrandBase>>({
    queryKey: BRAND_KEYS.list(params),
    queryFn: () => BrandApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

export const useBrandItemList = (params?: BrandItemListParams) => {
  const hasNameFilter = Boolean(params?.name && params.name.trim().length >= 1);
  const hasIdsFilter = Boolean(params?.ids && params.ids.length > 0);

  return useQuery<ApiResponse<BrandItem[]>, Error, BrandItem[] | undefined>({
    queryKey: BRAND_KEYS.itemList(params),
    queryFn: () => BrandApi.getItemList(params),
    enabled: hasNameFilter || hasIdsFilter,
    placeholderData: keepPreviousData,
    select: (data) => data.result ?? [],
  });
};

export const useBrandById = (id: number) => {
  return useQuery<ApiResponse<BrandById>>({
    queryKey: BRAND_KEYS.detail(id),
    queryFn: () => BrandApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateBrand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BrandCreatePayload) => BrandApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRAND_KEYS.all });
    },
  });
};

export const useUpdateBrand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BrandUpdatePayload) => BrandApi.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRAND_KEYS.all });
    },
  });
};

export const useDeleteBrand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => BrandApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRAND_KEYS.all });
    },
  });
};
